/* Wild-style product builder: composes real products (case + scent refills)
   into one purchase. One-time pricing is display-side here and enforced
   server-side by an automatic discount; subscription pricing/IDs come from
   the selling plans Liquid rendered into data-plans. */

if (!customElements.get('mov-builder')) {
  customElements.define(
    'mov-builder',
    class MovBuilder extends HTMLElement {
      connectedCallback() {
        // Guard against double-connect (theme-editor / dev hot reloads).
        if (this.movInitialized) return;
        this.movInitialized = true;
        this.mode = 'set';
        this.purchase = 'onetime';
        this.slots = parseInt(this.dataset.refillSlots, 10) || 3;
        this.barMax = parseInt(this.dataset.barMax, 10) || 5000;
        this.caseSetPrice = parseInt(this.dataset.caseSetPrice, 10) || 1200;
        this.caseSubDisplay = parseInt(this.dataset.caseSubDisplay, 10) || 1000;
        this.refillSubDisplay = parseInt(this.dataset.refillSubDisplay, 10) || 1040;
        const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD';
        this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        });
        // Same number, bare symbol: "14,95 $" rather than "14,95 $CA". Used
        // where an amount sits beside hand-written prices that carry no
        // currency code, so the two do not disagree in the same sentence.
        this.moneyShort = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
        });

        this.caseQty = 1;
        this.substick = this.querySelector('[data-substick]');
        this.caseEl = this.querySelector('[data-case]');
        this.case = this.readProduct(this.caseEl);
        this.scents = [...this.querySelectorAll('[data-scent]')].map((el) => ({
          el,
          handle: el.dataset.scent,
          name: el.dataset.name,
          desc: el.dataset.desc,
          soldOut: el.dataset.soldOut === 'true',
          qty: 0,
          ...this.readProduct(el),
        }));
        // A sold-out scent must never be the one the page opens on, or the
        // description panel and the "last picked" fallback both point at
        // something nobody can buy.
        this.lastScent = this.scents.find((s) => !s.soldOut) || null;

        this.buildFrequency();
        this.bind();
        this.bindSticky();
        this.bindCards();
        this.render();
      }

      readProduct(el) {
        const plans = (el.dataset.plans || '')
          .split('|')
          .filter(Boolean)
          .map((raw) => {
            const [id, name, price] = raw.split(':');
            return { id, name, price: parseInt(price, 10) };
          });
        return { variant: el.dataset.variant, otp: parseInt(el.dataset.otp, 10) || 0, plans };
      }

      bind() {
        this.querySelectorAll('[data-mode]').forEach((btn) =>
          btn.addEventListener('click', () => {
            this.mode = btn.dataset.mode;
            this.querySelectorAll('[data-mode]').forEach((b) => b.classList.toggle('is-active', b === btn));
            this.render();
          })
        );
        this.querySelectorAll('[data-purchase]').forEach((btn) =>
          btn.addEventListener('click', () => {
            this.purchase = btn.dataset.purchase;
            this.querySelectorAll('[data-purchase]').forEach((b) => b.classList.toggle('is-active', b === btn));
            this.render();
          })
        );
        this.scents.forEach((s) => {
          const stepper = s.el.querySelector('[data-stepper]');
          const qty = s.el.querySelector('[data-qty]');
          const media = s.el.querySelector('.mov-builder__scent-media');
          // The guard lives in the handler, not on the markup alone. A disabled
          // attribute is advisory — it stops a mouse, not a script or a stale
          // cached page — and the 2026-09-18 incident was precisely a scent
          // that could still be picked and then failed at the cart.
          s.el.querySelector('[data-add]').addEventListener('click', () => {
            if (s.soldOut) return;
            s.qty = 1;
            this.lastScent = s;
            this.render();
            this.feedback(stepper, 'is-popped', media);
          });
          s.el.querySelector('[data-plus]').addEventListener('click', () => {
            if (s.soldOut) return;
            s.qty += 1;
            this.lastScent = s;
            this.render();
            qty.classList.remove('is-ticked-down');
            this.feedback(qty, 'is-ticked', media);
          });
          s.el.querySelector('[data-minus]').addEventListener('click', () => {
            if (s.qty > 0) s.qty -= 1;
            this.render();
            qty.classList.remove('is-ticked');
            if (s.qty > 0) this.feedback(qty, 'is-ticked-down');
            else this.feedback(s.el.querySelector('[data-add]'), 'is-popped');
          });
          s.el.querySelector('.mov-builder__scent-media').addEventListener('click', () => {
            if (s.soldOut) return;
            this.lastScent = s;
            this.render();
          });
        });
        const casePlus = this.querySelector('[data-case-plus]');
        const caseMinus = this.querySelector('[data-case-minus]');
        if (casePlus) casePlus.addEventListener('click', () => { this.caseQty += 1; this.render(); });
        // − stops at 1 case: a tap there points at "Refills only" instead of doing nothing.
        if (caseMinus) caseMinus.addEventListener('click', () => {
          if (this.caseQty > 1) { this.caseQty -= 1; this.render(); }
          else this.feedback(this.querySelector('[data-case-hint]'), 'is-nudged');
        });
        this.querySelectorAll('[data-mode-link]').forEach((link) =>
          link.addEventListener('click', () => {
            const mode = this.querySelector(`[data-mode="${link.dataset.modeLink}"]`);
            if (!mode) return;
            mode.click();
            mode.closest('.mov-builder__modes')?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
          })
        );
        this.querySelector('[data-atc]').addEventListener('click', () => this.addToCart());

        // Free-stick widget: clicking it while locked flips the purchase
        // type to subscription (the perk then adds itself instantly).
        if (this.substick) {
          const activate = () => {
            if (this.purchase === 'sub' || this.mode === 'case' || !this.subPossible()) return;
            const subBtn = this.querySelector('[data-purchase="sub"]');
            if (subBtn) subBtn.click();
          };
          this.substick.addEventListener('click', activate);
          this.substick.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              activate();
            }
          });
        }
      }

      /* Mobile sticky CTA: mirrors the summary button, shows only while the
         real Add to cart is out of view. */
      bindSticky() {
        this.sticky = this.querySelector('[data-sticky]');
        if (!this.sticky) return;
        this.querySelector('[data-sticky-atc]').addEventListener('click', () => this.addToCart());
        const target = this.querySelector('[data-atc]');
        const io = new IntersectionObserver(
          ([entry]) => this.sticky.classList.toggle('is-visible', !entry.isIntersecting),
          { rootMargin: '0px 0px -10% 0px' }
        );
        io.observe(target);
      }

      /* Gift cards (bar_style "gifts"): tapping a gift node or pill opens a
         small card — photo, name, one line — instead of a dead tap. One card
         at a time; closes on its button, Escape, a second tap, or any tap
         outside the card. Without card markup (classic bar) this is inert. */
      bindCards() {
        const panels = [...this.querySelectorAll('[data-card-panel]')];
        if (!panels.length) return;
        const closeAll = () => {
          panels.forEach((p) => (p.hidden = true));
          this.querySelectorAll('[data-card]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
        };
        this.querySelectorAll('[data-card]').forEach((trigger) => {
          const toggle = (e) => {
            e.stopPropagation();
            const panel = this.querySelector(`[data-card-panel="${trigger.dataset.card}"]`);
            if (!panel) return;
            const wasOpen = !panel.hidden;
            closeAll();
            if (wasOpen) return;
            panel.hidden = false;
            trigger.setAttribute('aria-expanded', 'true');
          };
          trigger.addEventListener('click', toggle);
          trigger.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle(e);
            }
          });
        });
        panels.forEach((p) => {
          const close = p.querySelector('[data-card-close]');
          if (close) close.addEventListener('click', closeAll);
          p.addEventListener('click', (e) => e.stopPropagation());
        });
        document.addEventListener('click', closeAll);
        // iOS Safari and in-app browsers send no click for a tap on plain
        // content, but always send pointerdown. Taps on a trigger or inside
        // a card are left to their own click handlers.
        document.addEventListener('pointerdown', (e) => {
          if (!e.target.closest('[data-card], [data-card-panel]')) closeAll();
        });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') closeAll();
        });
      }

      totalQty() {
        return this.scents.reduce((n, s) => n + s.qty, 0);
      }

      buildFrequency() {
        // Union of plan names across all products (Appstle names plans per
        // frequency, e.g. "Every Month" — same names across products).
        const select = this.querySelector('[data-freq]');
        const seen = new Set();
        [this.case, ...this.scents].forEach((p) =>
          p.plans.forEach((plan) => {
            if (!seen.has(plan.name)) {
              seen.add(plan.name);
              const opt = document.createElement('option');
              opt.value = plan.name;
              opt.textContent = plan.name;
              select.appendChild(opt);
            }
          })
        );
        select.addEventListener('change', () => this.render());
      }

      /* Subscription applies to REFILLS only; the case never subscribes and
         Case-only cannot be bought on subscription at all. */
      subPossible() {
        if (this.mode === 'case') return false;
        if (this.scents.some((s) => s.qty > 0 && s.plans.length === 0)) return false;
        if (!this.scents.some((s) => s.plans.length > 0)) return false;
        return this.querySelector('[data-freq]').options.length > 0;
      }

      /* A milestone with data-modes only participates (bar node, unlock pill,
         perk line, next-goal message) in the bundle modes it lists. */
      milestoneVisible(m) {
        const modes = (m.dataset.modes || '').trim();
        return !modes || modes.split(/\s+/).includes(this.mode);
      }

      /* Shopify's cart errors arrive in three shapes: a plain string, an object
         keyed by field, or nothing useful at all. Its strings are already
         localized, so they are preferred over our own copy. */
      cartErrorMessage(state) {
        const e = state && state.errors;
        if (typeof e === 'string' && e.trim()) return e;
        if (e && typeof e === 'object') {
          const flat = Object.values(e).flat().filter(Boolean).join(' ');
          if (flat.trim()) return flat;
        }
        return state?.description || this.dataset.tCartError || 'Could not add to cart';
      }

      /* A perk the subscription grants unconditionally, whatever the total: the
         free 2nd stick, which the cart engine gives on any subscription holding
         a paid case, or on a one-time cart past the threshold. The rung has to
         speak for both, because the substick widget it used to share the job
         with is gone from the page — which is also why no perk can be counted
         twice any more: there is only one route left. */

      planFor(product) {
        const select = this.querySelector('[data-freq]');
        const name = select && select.value;
        return product.plans.find((p) => p.name === name) || product.plans[0] || null;
      }

      subPrice(product, fallback) {
        const plan = product.plans[0] ? this.planFor(product) : null;
        return plan ? plan.price : fallback;
      }

      /* Tap feedback on the scent controls: restart a one-shot CSS animation
         class (removing it and forcing a reflow lets repeated taps replay it),
         optionally bump the swatch, and give Android a light tick. */
      feedback(el, cls, media) {
        [[el, cls], [media, 'is-bumped']].forEach(([node, name]) => {
          if (!node) return;
          node.classList.remove(name);
          void node.offsetWidth;
          node.classList.add(name);
        });
        if (navigator.vibrate) navigator.vibrate(8);
      }

      /* A free perk line: quantity in blue, the value struck through, then $0.
         The gift line may carry several units (data-gift-qty, e.g. 2 sticks). */
      freeLine(label, value, qty = 1) {
        return `<div class="mov-builder__line mov-builder__line--gift"><span><strong class="mov-builder__line-qty">${qty} ×</strong> ${label}</span><span><s>${this.money.format(value / 100)}</s> ${this.money.format(0)}</span></div>`;
      }

      priceOf(product, isCase) {
        // The case is one-time only, at its flat configured price — it is
        // never part of the subscription, even in a subscribed Starter Set.
        if (isCase) return this.caseSetPrice;
        if (this.purchase === 'sub') {
          const plan = this.planFor(product);
          if (plan) return plan.price;
        }
        return product.otp;
      }

      render() {
        const includeCase = this.mode !== 'refills';
        const includeScents = this.mode !== 'case';

        this.querySelector('[data-step-case]').toggleAttribute('hidden', !includeCase);
        this.querySelector('[data-step-scents]').toggleAttribute('hidden', !includeScents);
        const caseMinus = this.querySelector('[data-case-minus]');
        if (caseMinus) {
          caseMinus.classList.toggle('is-disabled', this.caseQty <= 1);
          caseMinus.setAttribute('aria-disabled', String(this.caseQty <= 1));
        }
        const caseHint = this.querySelector('[data-case-hint]');
        if (caseHint) caseHint.hidden = this.mode !== 'set';
        this.querySelector('[data-scent-step-num]').textContent = includeCase ? '3' : '2';
        this.querySelector('[data-purchase-step-num]').textContent = includeCase && includeScents ? '4' : '3';

        const hint = this.querySelector('[data-scent-hint]');
        const picked = this.totalQty();
        // Supply hint only from 2+ refills — "[months] months" stays plural,
        // and one refill needs no duration pitch anyway.
        const supplyTpl = this.dataset.supplyHint || '';
        const pickedLabel = (this.dataset.tPicked || '[count] picked').replace('[count]', picked);
        hint.textContent =
          picked > 0
            ? pickedLabel + (supplyTpl && picked > 1 ? ` · ${supplyTpl.replace('[months]', picked)}` : '')
            : '';

        const setPerk = this.querySelector('[data-set-perk]');
        if (setPerk) setPerk.hidden = this.mode !== 'set';

        // The gifts hero widget follows the gift_set_only setting: when the
        // gifts are configured as set-only perks it hides outside Set mode.
        const giftsWidget = this.querySelector('.mov-builder__gifts');
        if (giftsWidget) giftsWidget.hidden = this.dataset.giftSetOnly === 'true' && this.mode !== 'set';

        // "From" prices = the minimal possible selection at the cheapest path:
        // case is flat one-time; refills cheapest on subscription.
        const prefix = this.dataset.fromPrefix || 'from';
        const subRefill = Math.min(...this.scents.map((s) => this.subPrice(s, this.refillSubDisplay)));
        this.querySelector('[data-from-set]').textContent =
          `${prefix} ${this.money.format((this.caseSetPrice + subRefill) / 100)}`;
        this.querySelector('[data-from-refills]').textContent = `${prefix} ${this.money.format(subRefill / 100)}`;
        this.querySelector('[data-from-case]').textContent = `${prefix} ${this.money.format(this.caseSetPrice / 100)}`;

        // Scent tiles + dynamic description panel (the stage is static —
        // its imagery and background come from theme-editor settings).
        this.scents.forEach((s) => {
          s.el.querySelector('[data-qty]').textContent = s.qty;
          s.el.querySelector('[data-add]').hidden = s.qty > 0;
          s.el.querySelector('[data-stepper]').hidden = s.qty === 0;
          s.el.classList.toggle('is-active', s.qty > 0);
          s.el.classList.toggle('is-featured', this.lastScent === s);
        });
        if (this.lastScent) {
          this.querySelector('[data-desc-name]').textContent = this.lastScent.name;
          this.querySelector('[data-desc-text]').textContent = this.lastScent.desc;
        }
        // Purchase availability (depends on which products the bundle needs).
        this.subAvailable = this.subPossible();
        const subBtn = this.querySelector('[data-purchase="sub"]');
        // Case-only: subscription isn't offered at all; snap back to one-time.
        subBtn.hidden = this.mode === 'case';
        if (this.mode === 'case' && this.purchase === 'sub') {
          this.purchase = 'onetime';
          this.querySelectorAll('[data-purchase]').forEach((b) =>
            b.classList.toggle('is-active', b.dataset.purchase === 'onetime')
          );
        }
        subBtn.classList.toggle('is-disabled', !this.subAvailable);
        this.querySelector('[data-sub-note]').hidden = this.subAvailable || this.purchase !== 'sub';
        this.querySelector('[data-freq-wrap]').hidden = this.purchase !== 'sub' || !this.subAvailable;
        // One-time carries the full refill price as its hint, so Subscribe &
        // Save's lower price has a visible anchor to beat.
        const otHint = this.dataset.onetimeHint || '';
        const otpRefill = this.scents.length ? Math.min(...this.scents.map((s) => s.otp)) : 0;
        this.querySelector('[data-onetime-hint]').textContent =
          this.mode === 'case' || !otHint || !otpRefill
            ? ''
            : otHint.replace('[price]', this.moneyShort.format(otpRefill / 100));

        // The configured subtitle pitches the free 2nd stick — a Starter-Set-
        // only perk. Refills-only swaps it for the per-refill subscription
        // price, reusing the (already localized) one-time hint template.
        // The case value next to the subtitle, struck through. The JS owns the
        // text because only it has the locale money formatter — Liquid would
        // print "14.95 $" in French where the summary says "14,95 $CA". It is
        // hidden in Refills only, where no case is bought and so none is free.
        const caseValueEl = subBtn.querySelector('[data-sub-case-value]');
        if (caseValueEl) {
          const cents = parseInt(caseValueEl.dataset.caseValue, 10) || 0;
          const show = cents > 0 && this.mode === 'set';
          caseValueEl.hidden = !show;
          if (show) caseValueEl.textContent = this.moneyShort.format(cents / 100);
        }

        const subSubEl = subBtn.querySelector('[data-sub-sub]');
        if (subSubEl) {
          if (subSubEl.dataset.original === undefined) subSubEl.dataset.original = subSubEl.textContent;
          if (this.mode === 'refills') {
            const subRefillPrice = this.scents.length
              ? Math.min(...this.scents.map((s) => this.subPrice(s, this.refillSubDisplay)))
              : 0;
            subSubEl.textContent =
              otHint && subRefillPrice ? otHint.replace('[price]', this.moneyShort.format(subRefillPrice / 100)) : '';
          } else {
            subSubEl.textContent = subSubEl.dataset.original;
          }
        }

        // Summary.
        this.compactLines = this.dataset.compactLines === 'true';
        const lines = [];
        let total = 0;
        let regularTotal = 0;
        const caseQtyEl = this.querySelector('[data-case-qty]');
        if (caseQtyEl) caseQtyEl.textContent = this.caseQty;
        const caseLabel = (this.dataset.tCaseLine || '[name] case').replace(
          '[name]',
          this.caseEl.querySelector('.mov-builder__case-name').textContent
        );
        if (includeCase) {
          const p = this.priceOf(this.case, true);
          lines.push({ label: caseLabel, qty: this.caseQty, price: p * this.caseQty });
          total += p * this.caseQty;
          regularTotal += p * this.caseQty;
        }
        // The subscription's free extra stick is the same Cobalt Blue case —
        // it renders directly under the paid case line, same name, at $0.
        // Starter Set only: the cart gift engine pairs the free stick with
        // the paid one, so modes that ship no paid case must not promise it.
        const stickOn = !!this.substick && this.purchase === 'sub' && this.subAvailable && this.mode === 'set';
        const stickValue = this.substick ? parseInt(this.substick.dataset.substickValue, 10) || 0 : 0;
        if (stickOn) {
          lines.push({ label: caseLabel, qty: 1, price: 0, full: stickValue, free: true });
          regularTotal += stickValue;
        }
        // On subscription each refill line keeps its regular price struck
        // through next to the subscription price; the difference lands in
        // the savings total with everything else.
        if (includeScents) {
          this.scents.forEach((s) => {
            if (s.qty > 0) {
              const p = this.priceOf(s, false);
              const full = s.otp * s.qty;
              const struck = this.purchase === 'sub' && full > p * s.qty;
              lines.push({ label: (this.dataset.tRefillLine || '[name] refill').replace('[name]', s.name), qty: s.qty, price: p * s.qty, full: struck ? full : 0 });
              total += p * s.qty;
              regularTotal += full;
            }
          });
        }
        let linesHtml = lines
          .map(
            (l) =>
              `<div class="mov-builder__line${l.free ? ' mov-builder__line--gift' : ''}"><span><strong class="mov-builder__line-qty">${l.qty} ×</strong> ${l.label}</span><span>${l.full ? `<s>${this.money.format(l.full / 100)}</s> ` : ''}${this.money.format(l.price / 100)}</span></div>`
          )
          .join('');
        // Free gift rides the order: $0.00 line whose value counts as savings.
        const giftLabel = this.dataset.giftLabel;
        const giftValue = parseInt(this.dataset.giftValue, 10) || 0;
        if (giftLabel && total > 0 && (this.dataset.giftSetOnly !== 'true' || this.mode === 'set')) {
          linesHtml += this.freeLine(giftLabel, giftValue, parseInt(this.dataset.giftQty, 10) || 1);
          regularTotal += giftValue;
        }

        // Every unlocked perk with a value rides the order the same way:
        // a $0.00 line with its value struck through, counted into savings.
        // Milestones render in two bars (top + summary) so dedupe by key.
        // Unlock thresholds always compare against the pre-discount subtotal.
        const subActive = this.purchase === 'sub';
        const seenPerks = new Set();
        const percentOffs = [];
        this.querySelectorAll('[data-milestone]').forEach((m) => {
          if (!this.milestoneVisible(m)) return;
          const value = parseInt(m.dataset.value, 10) || 0;
          const discount = parseInt(m.dataset.discount, 10) || 0;
          if (!value && !discount) return;
          const key = `${m.dataset.amount}|${m.dataset.label}`;
          if (seenPerks.has(key)) return;
          seenPerks.add(key);
          const amount = parseInt(m.dataset.amount, 10) || 0;
          if (m.dataset.subOnly === 'true' && !subActive) return;
          if (total <= 0) return;
          if (total < amount) return;
          if (value) {
            linesHtml += this.freeLine(this.compactLines && m.dataset.summaryLabel ? m.dataset.summaryLabel : m.dataset.label, value);
            regularTotal += value;
          }
          if (discount) percentOffs.push({ label: m.dataset.label, pct: discount });
        });

        // Free-stick widget: locked on one-time purchase, unlocked the moment
        // subscription is selected. Its summary line rides with the case
        // lines above (same product, $0) — here we only manage widget state.
        if (this.substick) {
          this.substick.hidden = this.mode !== 'set';
          this.substick.classList.toggle('is-unlocked', stickOn);
          this.substick.classList.toggle('is-locked', !stickOn);
          this.substick.setAttribute('aria-pressed', stickOn ? 'true' : 'false');
          const stickMsg = this.substick.querySelector('[data-substick-msg]');
          if (stickMsg) {
            stickMsg.textContent = stickOn ? this.substick.dataset.unlockedMsg : this.substick.dataset.lockedMsg;
          }
        }

        // Starter Sets ship free — the summary carries it as a FREE line whose
        // value rides into savings like the other perks. Not a bar milestone.
        const freeshipValue = parseInt(this.dataset.freeshipValue, 10) || 0;
        const freeshipLabel = this.dataset.freeshipLabel || '';
        if (this.mode === 'set' && freeshipLabel && freeshipValue > 0 && total > 0) {
          linesHtml += this.freeLine(freeshipLabel, freeshipValue);
          regularTotal += freeshipValue;
        }

        // Percentage milestones come off the running total last, as their own
        // summary line; the reduction lands in savings automatically. Only the
        // highest unlocked percentage applies — tiers upgrade, they don't
        // stack (mirrors the tiered automatic order discount at checkout).
        let payTotal = total;
        const bestOff = percentOffs.reduce((a, d) => (a && a.pct >= d.pct ? a : d), null);
        if (bestOff) {
          // Shopify applies the tier as one cart-level discount rounded DOWN to
          // the cent (measured 2026-09-24: 5085 -> 508, never 509). Rounding
          // here would show a total 1 cent off what checkout charges.
          const off = Math.floor((payTotal * bestOff.pct) / 100);
          if (off > 0) {
            linesHtml += `<div class="mov-builder__line mov-builder__line--discount"><span>${bestOff.label}</span><span>−${this.money.format(off / 100)}</span></div>`;
            payTotal -= off;
          }
        }
        this.querySelector('[data-summary-lines]').innerHTML = linesHtml;

        const savings = regularTotal - payTotal;
        const savingsEl = this.querySelector('[data-savings]');
        savingsEl.hidden = savings <= 0;
        if (savings > 0) {
          const pct = regularTotal > 0 ? Math.round((savings / regularTotal) * 100) : 0;
          this.querySelector('[data-savings-amount]').textContent =
            `${this.money.format(savings / 100)} (${pct}%)`;
        }


        // Mode-aware milestones: hide nodes/pills not meant for this bundle
        // mode, re-space the visible bar nodes evenly, and rebuild the fill
        // anchors so the progress edge stays truthful per mode.
        this.querySelectorAll('[data-milestone]').forEach((m) => {
          m.hidden = !this.milestoneVisible(m);
        });
        // A mode with no milestones at all (e.g. Case only) drops the whole
        // bar — an empty track with no goals is just noise.
        const anyMilestone = [...this.querySelectorAll('[data-milestone]')].some((m) => !m.hidden);
        this.querySelectorAll('.mov-builder__bar').forEach((bar) => (bar.hidden = !anyMilestone));
        // Nodes always sit in ascending dollar order, so the track reads left to
        // right in the order a shopper earns things. A perk that sorted ahead of
        // its amount put the free stick before the $20 shipping rung.
        const nodeRank = (m) => parseInt(m.dataset.amount, 10) || 0;
        this.querySelectorAll('[data-bar-track]').forEach((track) => {
          const nodes = [...track.querySelectorAll('[data-milestone]')]
            .filter((m) => !m.hidden)
            .sort((a, b) => nodeRank(a) - nodeRank(b));
          // The gift photos bar sits further in (9%–91%) so its wider end labels
          // and floating product shots never touch the panel edge.
          const inset = track.closest('.mov-builder__bar--gifts') ? 9 : 6;
          nodes.forEach((m, i) => {
            const nodePos = nodes.length > 1 ? Math.round((inset + (i * (100 - 2 * inset)) / (nodes.length - 1)) * 10) / 10 : 50;
            m.dataset.pos = nodePos;
            m.style.left = nodePos + '%';
            const amt = m.querySelector('[class$="node-amt"]');
            if (!amt) return;
            if (amt.dataset.origText === undefined) amt.dataset.origText = amt.textContent;
            amt.textContent = amt.dataset.origText;
          });
        });
        // The pill row under the track is flex, so it follows the same order.
        this.querySelectorAll('.mov-builder__unlocks').forEach((row) => {
          [...row.querySelectorAll('[data-milestone]')]
            .filter((m) => !m.hidden)
            .sort((a, b) => nodeRank(a) - nodeRank(b))
            .forEach((m, i) => {
              m.style.order = String(i);
            });
        });
        this.fillAnchors = null;

        // Milestone bars (top + summary duplicate). The fill keeps a 5%
        // baseline so the bar never reads empty (endowed progress), and is
        // interpolated through the node positions so it stays truthful to
        // nodes that were shifted apart for spacing.
        const pct = Math.min(Math.max(this.fillPct(total), 5), 100);
        this.querySelectorAll('[data-bar-fill]').forEach((f) => (f.style.width = pct + '%'));
        this.querySelectorAll('[data-bar-track]').forEach((t) => {
          t.setAttribute('aria-valuenow', Math.round(total / 100));
          t.setAttribute('aria-valuetext', this.money.format(total / 100));
        });
        let next = null;
        const isSub = this.purchase === 'sub';
        this.querySelectorAll('[data-milestone]').forEach((m) => {
          if (m.hidden) {
            m.classList.remove('is-locked', 'is-reached');
            return;
          }
          const amount = parseInt(m.dataset.amount, 10) || 0;
          const subOnly = m.dataset.subOnly === 'true';
          const locked = subOnly && !isSub;
          // A $0 perk rides any paid item (the cart engine's rule): it lights
          // up with the first paid item, never on an empty kit, and is never
          // the next goal ("$0.00 away from …").
          const reached = !locked && total > 0 && total >= amount;
          m.classList.toggle('is-locked', locked);
          m.classList.toggle('is-reached', reached);
          if (!reached && !locked && amount > 0 && (next === null || amount < next.amount)) next = { amount, label: m.dataset.label };
        });
        this.querySelectorAll('[data-milestone]').forEach((m) => {
          m.classList.toggle(
            'is-next',
            !!next && parseInt(m.dataset.amount, 10) === next.amount && m.dataset.label === next.label
          );
        });
        const msgText = next
          ? (this.dataset.barNext || '[amount] away from [label]')
              .replace('[amount]', this.money.format((next.amount - total) / 100))
              .replace('[label]', next.label)
          : this.dataset.barDone || 'All perks unlocked! 🎉';
        this.querySelectorAll('[data-bar-msg]').forEach((m) => (m.textContent = msgText));
        this.querySelectorAll('[data-bar-hint]').forEach((h) => (h.hidden = next === null));

        // The total anchors against the full regular value: struck compare-at
        // first, what they actually pay after it.
        const totalHtml =
          (regularTotal > payTotal ? `<s>${this.money.format(regularTotal / 100)}</s> ` : '') +
          this.money.format(payTotal / 100);
        this.querySelector('[data-total]').innerHTML = totalHtml;

        // Effective-cost chips: what a refill really costs after the best
        // triggered percentage tier (gifts/shipping values excluded), plus
        // the per-day price that follows. Shown for both purchase types —
        // the configurable note ("cancel anytime") only rides subscription.
        const permonth = this.querySelector('[data-permonth]');
        if (permonth) {
          const daysPerRefill = parseInt(this.dataset.daysPerRefill, 10) || 28;
          const showCost =
            this.mode !== 'case' && picked > 0 && (this.purchase !== 'sub' || this.subAvailable);
          permonth.hidden = !showCost;
          if (showCost) {
            const refillsSubtotal = total - (includeCase ? this.caseSetPrice * this.caseQty : 0);
            const tierPct = bestOff ? bestOff.pct : 0;
            const perRefill = (refillsSubtotal * (100 - tierPct)) / 100 / picked;
            const perDay = perRefill / daysPerRefill;
            const note =
              subActive && this.dataset.subMonthNote
                ? `<span class="mov-builder__cost-note">${this.dataset.subMonthNote}</span>`
                : '';
            permonth.innerHTML =
              `<span class="mov-builder__cost-chip"><strong>${this.money.format(perRefill / 100)}</strong>${this.dataset.tPerRefill || '/refill'}</span>` +
              `<span class="mov-builder__cost-chip"><strong>${this.money.format(perDay / 100)}</strong>${this.dataset.tPerDay || '/day'}</span>` +
              note;
          }
        }

        const caseOnce = this.querySelector('[data-case-once]');
        if (caseOnce) caseOnce.hidden = !includeCase;

        const valid = (this.mode === 'case' || picked > 0) && (this.purchase !== 'sub' || this.subAvailable);
        this.valid = valid;
        this.querySelector('[data-atc]').disabled = !valid || this.adding;
        const stickyAtc = this.querySelector('[data-sticky-atc]');
        if (stickyAtc) {
          stickyAtc.disabled = !valid || this.adding;
          this.querySelector('[data-sticky-total]').innerHTML = totalHtml;
        }
      }

      // Maps a cart total to a fill percentage by interpolating between the
      // rendered node positions (data-pos), so the fill edge always agrees
      // with the nodes even where their spacing was adjusted.
      fillPct(total) {
        if (!this.fillAnchors) {
          const track = this.querySelector('[data-bar-track]');
          const points = [{ a: 0, p: 0 }];
          if (track) {
            [...track.querySelectorAll('[data-milestone]')]
              .filter((m) => !m.hidden)
              .map((m) => ({ a: parseInt(m.dataset.amount, 10) || 0, p: parseFloat(m.dataset.pos) }))
              .filter((pt) => !isNaN(pt.p))
              .sort((x, y) => x.a - y.a)
              .forEach((pt) => {
                if (pt.a > points[points.length - 1].a) points.push(pt);
              });
          }
          if (points[points.length - 1].a < this.barMax) points.push({ a: this.barMax, p: 100 });
          this.fillAnchors = points;
        }
        const pts = this.fillAnchors;
        const t = Math.min(total, pts[pts.length - 1].a);
        for (let i = 1; i < pts.length; i++) {
          if (t <= pts[i].a) {
            return pts[i - 1].p + ((t - pts[i - 1].a) / (pts[i].a - pts[i - 1].a)) * (pts[i].p - pts[i - 1].p);
          }
        }
        return 100;
      }

      /* Locks both add buttons (summary + mobile sticky) while a cart request
         is in flight. Slow in-app browsers invite repeat taps, and each extra
         tap would add the whole kit again. */
      setAdding(on) {
        this.adding = on;
        [this.querySelector('[data-atc]'), this.querySelector('[data-sticky-atc]')].forEach((btn) => {
          if (!btn) return;
          btn.disabled = on || !this.valid;
          btn.classList.toggle('is-loading', on);
        });
      }

      /* Offer split (spec 2026-09-28): an offer page tells the cart which any-order gift it promised (cart
         attribute mov_offer, read by snippets/mov-cart-gift.liquid). Sent before the add so the drawer that opens
         already shows the right gifts. A failed stamp never blocks the add: the cart follows the theme default.
         No offer on the page = null, and the add goes out immediately, exactly as before the split. */
      stampOffer() {
        const offer = this.dataset.offerKey;
        if (offer !== 'soap' && offer !== 'siip') return null;
        return fetch(routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { mov_offer: offer } }),
        }).catch(() => null);
      }

      addToCart() {
        if (this.adding) return;
        const items = [];
        const sub = this.purchase === 'sub';
        if (this.mode !== 'refills') {
          // Case ships one-time only — never on a selling plan.
          items.push({ id: Number(this.case.variant), quantity: this.caseQty });
        }
        if (this.mode !== 'case') {
          this.scents.forEach((s) => {
            if (s.qty > 0) {
              const item = { id: Number(s.variant), quantity: s.qty };
              if (sub) {
                const plan = this.planFor(s);
                if (plan) item.selling_plan = Number(plan.id);
              }
              items.push(item);
            }
          });
        }
        this.setAdding(true);
        const add = () =>
          fetch(routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, sections: ['cart-drawer', 'cart-icon-bubble'] }),
          });
        const stamp = this.stampOffer();
        (stamp ? stamp.then(add) : add())
          .then((r) => r.json().then((state) => ({ ok: r.ok, state })))
          .then(({ ok, state }) => {
            // Shopify answers a sold-out add with HTTP 422 and a body shaped
            // {"errors": "...plus en stock"} — carrying NO `status` field. The
            // old check looked only at state.status, so that response sailed
            // through as success: no message, no drawer, button re-enabled.
            // It ran ~30 hours on 2026-09-18 and cost a 1.1% day.
            if (!ok || state.errors || (state.status && state.status !== 200)) {
              throw new Error(this.cartErrorMessage(state));
            }
            const drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.renderContents === 'function' && state.sections) drawer.renderContents(state);
            // Announce through Dawn's pub/sub so theme listeners and any
            // analytics hooks treat this like a native add-to-cart.
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mov-builder', cartData: state });
            }
            this.querySelector('[data-error]').hidden = true;
          })
          .catch((err) => {
            const box = this.querySelector('[data-error]');
            box.textContent = err.message;
            box.hidden = false;
          })
          .finally(() => this.setAdding(false));
      }
    }
  );
}

/* Media gallery: slide 1 is the interactive stage, the rest are product
   images. Native scroll-snap does the swiping; this only wires arrows,
   dots/thumbs, and active-state sync. */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initGallery = (gallery) => {
    if (gallery.movGalleryInit) return;
    gallery.movGalleryInit = true;

    const track = gallery.querySelector('[data-gallery-track]');
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const media = gallery.closest('.mov-builder__media') || gallery;
    const gotos = [...media.querySelectorAll('[data-gallery-goto]')];
    const prev = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    if (!track || slides.length < 2) return;

    let index = 0;

    const setActive = (i) => {
      if (i === index) return;
      index = i;
      gotos.forEach((btn) => {
        const active = parseInt(btn.dataset.galleryGoto, 10) === i;
        btn.classList.toggle('is-active', active);
        if (active) {
          btn.setAttribute('aria-current', 'true');
          const strip = btn.closest('[data-gallery-thumbs]');
          // Keep the active thumb in view by moving only the strip itself:
          // scrollIntoView would cancel the track's in-flight smooth scroll.
          if (strip) strip.scrollLeft = btn.offsetLeft - (strip.clientWidth - btn.offsetWidth) / 2;
        } else {
          btn.removeAttribute('aria-current');
        }
      });
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    };

    const goTo = (i) => {
      const clamped = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: clamped * track.clientWidth, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    if (prev) prev.addEventListener('click', () => goTo(index - 1));
    if (next) next.addEventListener('click', () => goTo(index + 1));
    gotos.forEach((btn) => btn.addEventListener('click', () => goTo(parseInt(btn.dataset.galleryGoto, 10))));

    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
    });

    track.addEventListener(
      'scroll',
      () => setActive(Math.round(track.scrollLeft / track.clientWidth)),
      { passive: true }
    );

    if (prev) prev.disabled = true;
  };

  const initAll = (root) => root.querySelectorAll('[data-gallery]').forEach(initGallery);
  initAll(document);
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
